import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, PlugZap, Unplug } from "lucide-react";
import {
  SOCIAL_PLATFORMS,
  type AccountDTO,
  type SocialConnectionDTO,
  type SocialPlatformDef,
} from "@app/shared";
import { api, socialOauthStartUrl } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { Button, buttonVariants } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useToast } from "./Toast";

function msg(e: unknown): string {
  return e instanceof Error ? e.message : "Terjadi kesalahan";
}

/**
 * Per-brand social connections panel: every supported platform (besides the
 * built-in Instagram + WordPress) with its status + connect/disconnect actions.
 * OAuth platforms redirect through the platform; Bluesky uses an app-password.
 */
export function SocialConnections({ brand }: { brand: AccountDTO }) {
  const query = useQuery({
    queryKey: ["social-connections", brand.key],
    queryFn: ({ signal }) => api.socialConnections(brand.key, signal),
  });

  // Index the live status by platform; fall back to the static registry order.
  const byKey = new Map((query.data ?? []).map((c) => [c.platform, c]));

  return (
    <details className="rounded-md border border-slate-200 bg-slate-50">
      <summary className="cursor-pointer select-none px-4 py-2.5 text-sm font-medium text-navy">
        Koneksi Sosial Media
      </summary>
      <div className="space-y-2 border-t border-slate-200 p-4">
        {query.isLoading ? (
          <p className="text-xs text-slate-500">Memuat…</p>
        ) : (
          SOCIAL_PLATFORMS.map((def) => {
            const conn = byKey.get(def.key);
            return (
              <PlatformRow
                key={def.key}
                brand={brand}
                def={def}
                conn={conn ?? null}
              />
            );
          })
        )}
        <p className="pt-1 text-xs text-slate-500">
          Instagram &amp; WordPress diatur di bagian di atas. Platform OAuth perlu
          kredensial aplikasi diset di server sebelum bisa dihubungkan.
        </p>
      </div>
    </details>
  );
}

function PlatformRow({
  brand,
  def,
  conn,
}: {
  brand: AccountDTO;
  def: SocialPlatformDef;
  conn: SocialConnectionDTO | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});

  const connected = conn?.connected ?? false;
  const configured = conn?.configured ?? def.authType === "credentials";

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ["social-connections", brand.key] });

  const connect = useMutation({
    mutationFn: () =>
      api.connectSocialCredentials(brand.key, def.key, { credentials: values }),
    onSuccess: () => {
      toast.success(`${def.label} terhubung`);
      setOpen(false);
      setValues({});
      invalidate();
    },
    onError: (e) => toast.error(msg(e)),
  });

  const disconnect = useMutation({
    mutationFn: () => api.disconnectSocial(brand.key, def.key),
    onSuccess: () => {
      toast.success(`${def.label} diputuskan`);
      invalidate();
    },
    onError: (e) => toast.error(msg(e)),
  });

  const fieldsFilled = (def.credentialFields ?? []).every((f) =>
    (values[f.key] ?? "").trim(),
  );

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="h-8 w-8 shrink-0 rounded-md"
            style={{ backgroundColor: def.color }}
            aria-hidden
          />
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900">{def.label}</p>
            <p className="truncate text-xs text-slate-500">
              {connected ? (
                <>
                  Terhubung
                  {conn?.externalName ? ` · ${conn.externalName}` : ""}
                  {conn?.expiresAt
                    ? ` · kedaluwarsa ${formatDateTime(conn.expiresAt)}`
                    : ""}
                </>
              ) : configured ? (
                "Belum terhubung"
              ) : (
                "Belum dikonfigurasi di server"
              )}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Badge variant={connected ? "green" : "coral"}>
            {connected ? "Connected" : "Disconnected"}
          </Badge>
          {connected ? (
            <Button
              variant="outline"
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
            >
              {disconnect.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Unplug className="h-4 w-4" />
              )}
              Putuskan
            </Button>
          ) : def.authType === "oauth2" ? (
            configured ? (
              // Full-page navigation so the browser follows the OAuth 302 chain.
              <a
                href={socialOauthStartUrl(brand.key, def.key)}
                className={buttonVariants({ variant: "primary" })}
              >
                <PlugZap className="h-4 w-4" />
                Hubungkan
              </a>
            ) : (
              <Button variant="primary" disabled>
                <PlugZap className="h-4 w-4" />
                Hubungkan
              </Button>
            )
          ) : (
            <Button variant="primary" onClick={() => setOpen((v) => !v)}>
              <PlugZap className="h-4 w-4" />
              Hubungkan
            </Button>
          )}
        </div>
      </div>

      {/* Credential form (e.g. Bluesky app-password) */}
      {open && def.authType === "credentials" && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          {(def.credentialFields ?? []).map((f) => (
            <div key={f.key} className="space-y-1">
              <Label htmlFor={`${def.key}-${f.key}`}>{f.label}</Label>
              <Input
                id={`${def.key}-${f.key}`}
                type={f.type}
                placeholder={f.placeholder}
                autoComplete="off"
                value={values[f.key] ?? ""}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, [f.key]: e.target.value }))
                }
              />
            </div>
          ))}
          <Button
            variant="primary"
            onClick={() => connect.mutate()}
            disabled={connect.isPending || !fieldsFilled}
          >
            {connect.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Simpan & hubungkan"
            )}
          </Button>
        </div>
      )}

      {def.note && (
        <p className="mt-2 text-[11px] leading-snug text-slate-400">{def.note}</p>
      )}
    </div>
  );
}
